import type { Generator, Load } from "./types.ts";

export interface Bus {
  id: string;
  name: string;
}

export type LineStatus = "ok" | "tripped";

export interface Line {
  id: string;
  from: string;             // bus id
  to: string;               // bus id
  susceptance: number;      // MW/rad (b = 1/x scaled by S_base)
  thermalLimit: number;     // MW
  status: LineStatus;
  flow: number;             // MW, from→to positive (computed)
  overloadTimer: number;    // game-minutes flow has exceeded thermalLimit
  trippedAt?: number;       // game-minutes when last tripped
}

export interface Network {
  buses: Bus[];
  lines: Line[];
  slackBus: string;
  // Mapping: which bus each gen/load lives on
  genBus: Map<string, string>;
  loadBus: Map<string, string>;
}

export interface PFResult {
  angles: Map<string, number>;    // bus → radians
  islanded: Set<string>;          // buses not connected to slack
  flowsByLine: Map<string, number>;
  busInjection: Map<string, number>; // bus → net MW (gen - load)
}

export function makeBus(id: string, name: string): Bus {
  return { id, name };
}

export function makeLine(spec: {
  id: string;
  from: string;
  to: string;
  susceptance?: number;
  thermalLimit: number;
}): Line {
  return {
    id: spec.id,
    from: spec.from,
    to: spec.to,
    susceptance: spec.susceptance ?? 200,
    thermalLimit: spec.thermalLimit,
    status: "ok",
    flow: 0,
    overloadTimer: 0,
  };
}

export function makeNetwork(spec: {
  buses: Bus[];
  lines: Line[];
  slackBus: string;
  genBus: Record<string, string>;
  loadBus: Record<string, string>;
}): Network {
  return {
    buses: spec.buses,
    lines: spec.lines,
    slackBus: spec.slackBus,
    genBus: new Map(Object.entries(spec.genBus)),
    loadBus: new Map(Object.entries(spec.loadBus)),
  };
}

/** Compute net injection at each bus given current generator outputs and load demands. */
export function busInjections(net: Network, gens: Generator[], loads: Load[], demandFor: (id: string) => number): Map<string, number> {
  const inj = new Map<string, number>();
  for (const b of net.buses) inj.set(b.id, 0);
  for (const g of gens) {
    const bus = net.genBus.get(g.id);
    if (!bus) continue;
    inj.set(bus, (inj.get(bus) ?? 0) + g.output);
  }
  for (const l of loads) {
    const bus = net.loadBus.get(l.id);
    if (!bus) continue;
    inj.set(bus, (inj.get(bus) ?? 0) - demandFor(l.id));
  }
  return inj;
}

/** DC power flow. Mutates lines (.flow). */
export function solveDCPF(net: Network, injection: Map<string, number>): PFResult {
  // Adjacency over alive lines
  const live = net.lines.filter((l) => l.status === "ok");
  const adj = new Map<string, string[]>();
  for (const b of net.buses) adj.set(b.id, []);
  for (const l of live) {
    adj.get(l.from)!.push(l.to);
    adj.get(l.to)!.push(l.from);
  }

  // Connected component containing slack
  const connected = new Set<string>([net.slackBus]);
  const q = [net.slackBus];
  while (q.length) {
    const u = q.shift()!;
    for (const v of adj.get(u)!) if (!connected.has(v)) { connected.add(v); q.push(v); }
  }
  const islanded = new Set<string>();
  for (const b of net.buses) if (!connected.has(b.id)) islanded.add(b.id);

  // Build B matrix for connected non-slack buses
  const nonSlack = [...connected].filter((id) => id !== net.slackBus);
  const n = nonSlack.length;
  const idx = new Map<string, number>();
  nonSlack.forEach((id, i) => idx.set(id, i));

  const B: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));
  for (const l of live) {
    if (!connected.has(l.from) || !connected.has(l.to)) continue;
    const i = idx.get(l.from);
    const j = idx.get(l.to);
    if (i !== undefined) B[i]![i]! += l.susceptance;
    if (j !== undefined) B[j]![j]! += l.susceptance;
    if (i !== undefined && j !== undefined) {
      B[i]![j]! -= l.susceptance;
      B[j]![i]! -= l.susceptance;
    }
  }

  const P: number[] = nonSlack.map((id) => injection.get(id) ?? 0);
  const theta = n > 0 ? gaussSolve(B, P) : [];

  const angles = new Map<string, number>();
  angles.set(net.slackBus, 0);
  nonSlack.forEach((id, i) => angles.set(id, theta[i] ?? 0));

  // Flows
  const flowsByLine = new Map<string, number>();
  for (const l of net.lines) {
    if (l.status === "tripped" || islanded.has(l.from) || islanded.has(l.to) || !connected.has(l.from) || !connected.has(l.to)) {
      l.flow = 0;
      flowsByLine.set(l.id, 0);
    } else {
      const tf = angles.get(l.from) ?? 0;
      const tt = angles.get(l.to) ?? 0;
      l.flow = l.susceptance * (tf - tt);
      flowsByLine.set(l.id, l.flow);
    }
  }

  return { angles, islanded, flowsByLine, busInjection: injection };
}

/** Gaussian elimination with partial pivoting. */
function gaussSolve(A: number[][], b: number[]): number[] {
  const n = b.length;
  if (n === 0) return [];
  const M: number[][] = A.map((row, i) => [...row, b[i]!]);
  for (let k = 0; k < n; k++) {
    let pivot = k;
    for (let i = k + 1; i < n; i++) {
      if (Math.abs(M[i]![k]!) > Math.abs(M[pivot]![k]!)) pivot = i;
    }
    if (pivot !== k) { const tmp = M[k]!; M[k] = M[pivot]!; M[pivot] = tmp; }
    if (Math.abs(M[k]![k]!) < 1e-12) continue;
    for (let i = k + 1; i < n; i++) {
      const f = M[i]![k]! / M[k]![k]!;
      for (let j = k; j <= n; j++) M[i]![j]! -= f * M[k]![j]!;
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let s = M[i]![n]!;
    for (let j = i + 1; j < n; j++) s -= M[i]![j]! * x[j];
    x[i] = Math.abs(M[i]![i]!) < 1e-12 ? 0 : s / M[i]![i]!;
  }
  return x;
}
